import type { PermissionKey, PortalMeResponse, TenantBrandingResponse } from '@servicetim/shared';
import { usePortalMe, useTenantBranding } from '@servicetim/ui/api';

/*
 * Portal-side helpers over the metadata hooks in `@servicetim/ui/api` (`usePortalProjects`,
 * `usePortalRequestTypes`, `usePortalResolvedForm`, `useTenantBranding`, `usePortalMe`):
 * branding defaults and permission lookups the screens share.
 */

/* ----------------------------- branding ----------------------------- */

export interface PortalBranding {
  tenantName: string;
  /** Help-center title shown in the header and the document title. */
  portalName: string;
  productName: string;
  logoUrl: string | null;
  primaryColor: string | null;
}

export const DEFAULT_BRANDING: PortalBranding = {
  tenantName: 'ServiceTim',
  portalName: 'Help center',
  productName: 'ServiceTim',
  logoUrl: null,
  primaryColor: null,
};

/** `GET /v1/tenant/branding` with every optional filled in (portal name falls back to "<tenant> help center"). */
export function normalizeBranding(raw: TenantBrandingResponse | null | undefined): PortalBranding {
  if (!raw) return DEFAULT_BRANDING;
  const branding = raw.settings.branding ?? {};
  const tenantName = raw.name.length > 0 ? raw.name : DEFAULT_BRANDING.tenantName;
  return {
    tenantName,
    portalName: branding.portalName ?? `${tenantName} help center`,
    productName: branding.productName ?? DEFAULT_BRANDING.productName,
    logoUrl: branding.logoUrl ?? null,
    primaryColor: branding.primaryColor ?? null,
  };
}

/** Branding query + normalized value (defaults while loading or on error, so the shell never waits). */
export function usePortalBranding() {
  const query = useTenantBranding();
  return { ...query, branding: normalizeBranding(query.data) };
}

/* ----------------------------- permissions (from GET /v1/me) ----------------------------- */

/** True when the caller holds `permission` in at least one visible project. */
export const hasPermissionSomewhere = (
  me: PortalMeResponse | null | undefined,
  permission: PermissionKey,
): boolean => me?.projects.some((p) => p.permissions.includes(permission)) ?? false;

export const projectPermissions = (
  me: PortalMeResponse | null | undefined,
  projectId: string,
): ReadonlySet<PermissionKey> =>
  new Set(me?.projects.find((p) => p.id === projectId)?.permissions ?? []);

/** Nav gating: the caller may see organization tickets somewhere and belongs to an organization. */
export function useCanSeeOrganization(): boolean {
  const me = usePortalMe();
  return (
    hasPermissionSomewhere(me.data, 'ticket.view_org') && (me.data?.organizations.length ?? 0) > 0
  );
}
